/**
 * ProfilesInfoModal
 *
 * Modal that explains what each profile/role means.
 * Shows descriptions for all available profiles based on the
 * permission matrix.
 *
 * ## Component Hierarchy
 * - **Level**: Organism
 * - **Feature**: Users
 * - **Location**: `src/features/users/components/ui/organisms/ProfilesInfoModal/`
 *
 * ## Features
 * - Lists all available profiles with descriptions
 * - Groups by profile type (Internal/External)
 * - Accessible design with proper labels
 *
 * @module features/users/components/ui/organisms/ProfilesInfoModal
 */
import React from 'react';
import { Modal, Typography } from 'antd';
import { useLanguage } from '@/shared/hooks/useLanguage';

import './ProfilesInfoModal.css';

const { Text, Paragraph } = Typography;

export interface ProfilesInfoModalProps {
  /** Whether the modal is visible */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
}

interface ProfileDescription {
  name: string;
  descriptionKey: string;
}

const EXTERNAL_PROFILES: ProfileDescription[] = [
  {
    name: 'Admin Avançado',
    descriptionKey:
      'Full permissions; full access; manages users; sends invitations for any role. Only one user can have this role.',
  },
  {
    name: 'Admin Básico',
    descriptionKey:
      'Operator, Supervisor, and Manager permissions; creates users up to Manager level; manages users.',
  },
  {
    name: 'Gestor',
    descriptionKey:
      'Operator/Supervisor functions; accesses dashboards; manages users; sends invitations to subordinates. Accesses all configured entities.',
  },
  {
    name: 'Supervisor',
    descriptionKey: 'Advanced Viewer permissions; views users.',
  },
  {
    name: 'Operador',
    descriptionKey:
      'Performs daily queries, views and exports their own queries, with no access to management features.',
  },
];

/**
 * ProfilesInfoModal Component
 *
 * @param props - Component properties
 * @returns React component
 *
 * @example
 * ```tsx
 * <ProfilesInfoModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 * />
 * ```
 */
export const ProfilesInfoModal: React.FC<ProfilesInfoModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useLanguage();

  return (
    <Modal
      open={open}
      title={t('Understand the Permissions')}
      onCancel={onClose}
      footer={null}
      width={700}
      destroyOnHidden
      className="profiles-info-modal"
      aria-label={t('Profiles information modal')}
    >
      <div className="profiles-info-modal__content">
        <div className="profiles-info-modal__grid">
          {EXTERNAL_PROFILES.map((profile) => (
            <div key={profile.name} className="profiles-info-modal__card">
              <Text strong className="profiles-info-modal__card-title">
                {t(profile.name)}
              </Text>
              <Paragraph className="profiles-info-modal__card-description">
                {t(profile.descriptionKey)}
              </Paragraph>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};

export default ProfilesInfoModal;
